import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../shared/useNotifications';
import ForgotPasswordModal from './ForgotPasswordModal';
import './AuthShared.css';

const OrganizerLogin = ({ onBackToHome, forcedError, onErrorDisplayed }) => {
  const navigate = useNavigate();
  const { signIn, signUp, signOut } = useAuth();
  const { success: showSuccess, error: showError } = useNotifications();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Mostrar error forzado si viene del LoginView
  useEffect(() => {
    if (forcedError) {
      setError(forcedError);
    }
  }, [forcedError]);

  // Resetear error cuando el usuario empieza a escribir
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
      if (onErrorDisplayed) onErrorDisplayed();
    }
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) {
      setError('');
      if (onErrorDisplayed) onErrorDisplayed();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    try {
      const { data, error: authError } = await signIn(email, password);

      if (authError) {
        setError(authError);
        setLoading(false);
        return;
      }

      // La navegación se maneja desde OrganizerLoginView via useEffect
    } catch (err) {
      console.error('Error en login:', err);
      setError('Error al iniciar sesión. Intenta nuevamente.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!name.trim() || !email.trim() || !password) {
      setError('Nombre, correo y contraseña son obligatorios');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const { data: authDataWithProfile, error: authErrorWithProfile } = await signUp(
        email.trim(),
        password,
        'organizer',
        {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          organization: organization.trim() || null,
          is_active: false,
          approval_status: 'pending_approval',
        }
      );

      if (authErrorWithProfile) {
        setError(authErrorWithProfile);
        setLoading(false);
        return;
      }

      const organizerData = authDataWithProfile?.profile;
      if (!organizerData) {
        setError('Error guardando el perfil del organizador. Intenta nuevamente.');
        setLoading(false);
        return;
      }

      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'organizer-registration',
            organizerName: name.trim(),
            organizerEmail: email.trim().toLowerCase(),
            organizerPhone: phone.trim(),
            organizerOrganization: organization.trim(),
          }),
        });
      } catch (emailErr) {
        console.warn('No se pudo enviar notificación al admin:', emailErr);
      }

      showSuccess('¡Solicitud enviada para aprobación!');
      // Cerrar sesión: el organizador está pendiente de aprobación
      try { await signOut(); } catch (_) {}
      setStep('success');
    } catch (err) {
      console.error('Error en registro:', err);
      setError('Error al registrar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-success">
            <h2>¡Solicitud Enviada!</h2>
            <p>Tu registro como organizador se ha enviado para aprobación.</p>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Revisa tu correo: el administrador te habilitará el acceso cuando apruebe tu cuenta.
            </p>
            <button
              type="button"
              onClick={() => { setMode('login'); setStep('form'); setError(''); }}
              className="auth-btn-primary"
            >
              Ir a Inicio de Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button
          type="button"
          onClick={onBackToHome}
          className="auth-back"
          aria-label="Volver"
        >
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="auth-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="auth-logo" onClick={() => navigate('/')} />
          <h2>{mode === 'login' ? 'Acceso Organizador' : 'Registro de Organizador'}</h2>
          <p>{mode === 'login' ? 'Ingresa con tus credenciales' : 'Crea tu cuenta de organizador'}</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="auth-form">
          {mode === 'register' && (
            <>
              <div className="auth-form-group">
                <label>Nombre completo *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                />
              </div>
              <div className="auth-form-group">
                <label>Organización</label>
                <input
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Nombre de tu organización (opcional)"
                />
              </div>
              <div className="auth-form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </>
          )}

          <div className="auth-form-group">
            <label>Correo electrónico *</label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="correo@ejemplo.com"
              autoComplete="email"
            />
          </div>

          <div className="auth-form-group">
            <label>Contraseña *</label>
            <input
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="••••••••"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>

          {mode === 'register' && (
            <div className="auth-form-group">
              <label>Confirmar contraseña *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="auth-btn-primary"
          >
            {loading ? (
              <span className="auth-spinner" />
            ) : mode === 'login' ? (
              'Iniciar Sesión'
            ) : (
              'Crear Cuenta'
            )}
          </button>
        </form>

        <div className="auth-footer">
          {mode === 'login' ? (
            <>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                style={{ display: 'block', margin: '0 auto 12px', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                ¿Olvidaste tu contraseña?
              </button>
              <p>
                ¿No tienes cuenta?{' '}
                <button type="button" onClick={() => { setMode('register'); setError(''); }}>
                  Regístrate aquí
                </button>
              </p>
            </>
          ) : (
            <p>
              ¿Ya tienes cuenta?{' '}
              <button type="button" onClick={() => { setMode('login'); setError(''); }}>
                Inicia sesión
              </button>
            </p>
          )}
        </div>

        <p className="auth-copyright">
          © {new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.
        </p>
      </div>
      <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
    </div>
  );
};

export default OrganizerLogin;
